import { useEffect, useState } from 'react';
import { ChevronDown, Cookie, Settings2, ShieldCheck, X } from 'lucide-react';
import {
  getOptionalIntegrations,
  useConsent,
  type ConsentCategory,
  type ConsentPreferences,
  type OptionalIntegration,
} from '@/context/ConsentContext';

const CATEGORY_COPY = {
  necessary: {
    label: 'Niezbędne',
    description: 'Są potrzebne, aby strona, konto i zamówienie działały prawidłowo.',
    details: 'Te pliki i mechanizmy są aktywne zawsze, ponieważ bez nich nie moglibyśmy bezpiecznie obsłużyć podstawowych funkcji serwisu. Pomagają utrzymać sesję, zawartość koszyka, logowanie, płatność i ochronę przed nadużyciami. Dostawcy powiązani z tą kategorią to Shopify, Stripe, Google (wyłącznie logowanie) oraz Cloudflare. Nie można ich wyłączyć w ustawieniach cookies.',
    providers: ['Shopify', 'Stripe', 'Google (wyłącznie logowanie)', 'Cloudflare'],
  },
  functional: {
    label: 'Funkcjonalne',
    description: 'Włączają dodatkowe udogodnienia, takie jak mapa lokalizacji kwiaciarni.',
    details: 'Ta kategoria jest aktywna tylko wtedy, gdy wyrazisz na nią zgodę. Obejmuje dodatkowe elementy, które ułatwiają korzystanie ze strony, w szczególności osadzoną mapę lokalizacji naszych kwiaciarni. Po wycofaniu zgody mapa nie zostanie załadowana, ale pozostałe funkcje sklepu nadal będą działać.',
    providers: [],
  },
  analytics: {
    label: 'Wydajność/Analityczne',
    description: 'Pomagają sprawdzać, czy strona działa wygodnie i sprawnie.',
    details: 'Po Twojej zgodzie Google Analytics pomaga nam poznawać źródła odwiedzin i poprawiać sklep. Mierzymy odwiedzane strony, oglądane produkty, dodania do koszyka, rozpoczęcie zamówienia i potwierdzone zakupy. Przekazujemy identyfikatory produktów, ilości i kwoty, bez danych kontaktowych, adresu dostawy ani treści bilecika. Zgodę możesz wycofać w każdej chwili.',
    providers: [],
  },
  marketing: {
    label: 'Reklama/śledzenie',
    description: 'Dotyczą dopasowania komunikacji marketingowej i mierzenia reklam.',
    details: 'Po Twojej zgodzie Google Ads pomaga nam sprawdzać, które reklamy prowadzą do opłaconych zamówień. Google może zapisać identyfikator kliknięcia reklamy w cookies. Przekazujemy wartość, walutę i techniczny identyfikator opłaconego zamówienia; nie przekazujemy danych kontaktowych ani treści bilecika. Nie używamy tego pomiaru do personalizacji reklam. Zgoda nie jest potrzebna do zakupu i można ją w każdej chwili wycofać.',
    providers: [],
  },
} as const;

function CookieConsentCategory({
  category,
  checked,
  disabled = false,
  integrations,
  onChange,
}: {
  category: ConsentCategory;
  checked: boolean;
  disabled?: boolean;
  integrations: OptionalIntegration[];
  onChange?: (checked: boolean) => void;
}) {
  const [expanded, setExpanded] = useState(false);
  const copy = CATEGORY_COPY[category];
  const categoryIntegrations = integrations.filter(integration => integration.category === category);
  const sharedWith = [...new Set(categoryIntegrations.flatMap(integration => integration.sharedWith))];
  const providers = category === 'necessary' ? copy.providers : sharedWith;
  const detailsId = `cookie-consent-details-${category}`;

  return (
    <div className={`cookie-consent-option${expanded ? ' is-expanded' : ''}`}>
      <div className="cookie-consent-option-row">
        <button
          type="button"
          className="cookie-consent-expand-button"
          aria-expanded={expanded}
          aria-controls={detailsId}
          onClick={() => setExpanded(value => !value)}
        >
          <ChevronDown size={16} aria-hidden />
          <span>
            <strong>{copy.label}</strong>
            <small>{copy.description}</small>
          </span>
        </button>
        <label className="cookie-consent-toggle">
          <input
            type="checkbox"
            checked={checked}
            disabled={disabled}
            aria-label={copy.label}
            onChange={event => onChange?.(event.target.checked)}
          />
          <span className="cookie-consent-switch" aria-hidden>
            <span />
          </span>
        </label>
      </div>
      {expanded && (
        <div id={detailsId} className="cookie-consent-option-details">
          <p>{copy.details}</p>
          {providers.length > 0 && (
            <div className="cookie-consent-shared-with">
              <strong>{category === 'necessary' ? 'Dostawcy' : 'Współdzielone z'}</strong>
              <ul>
                {providers.map(service => <li key={service}>{service}</li>)}
              </ul>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CookieSettingsPanel() {
  const {
    preferences,
    closeSettings,
    acceptAll,
    savePreferences,
  } = useConsent();
  const [draft, setDraft] = useState<ConsentPreferences>(
    preferences ?? { necessary: true, functional: false, analytics: false, marketing: false },
  );

  useEffect(() => {
    setDraft(preferences ?? { necessary: true, functional: false, analytics: false, marketing: false });
  }, [preferences]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape') closeSettings();
    };
    document.body.classList.add('modal-open');
    window.addEventListener('keydown', onKeyDown);
    return () => {
      document.body.classList.remove('modal-open');
      window.removeEventListener('keydown', onKeyDown);
    };
  }, [closeSettings]);

  const integrations = getOptionalIntegrations();

  return (
    <div className="cookie-consent-dialog-layer" role="presentation">
      <button className="cookie-consent-backdrop" aria-label="Zamknij ustawienia cookies" onClick={closeSettings} />
      <section
        className="cookie-consent-dialog"
        role="dialog"
        aria-modal="true"
        aria-labelledby="cookie-settings-title"
      >
        <div className="cookie-consent-dialog-header">
          <div>
            <p className="cookie-consent-eyebrow"><Settings2 size={13} /> Ustawienia prywatności</p>
            <h2 id="cookie-settings-title">Twoje cookies</h2>
          </div>
          <button type="button" className="cookie-consent-icon-button" aria-label="Zamknij" onClick={closeSettings}>
            <X size={18} />
          </button>
        </div>

        <p className="cookie-consent-intro">
          Niezbędne pliki cookies działają zawsze, aby strona, koszyk, konto, płatności i bezpieczeństwo działały prawidłowo. Pozostałe kategorie są opcjonalne i uruchamiają się wyłącznie po Twojej zgodzie. Rozwiń kategorię, aby zobaczyć dokładny opis oraz powiązanych dostawców. Zgodę możesz zmienić lub wycofać w dowolnym momencie w ustawieniach cookies.
        </p>

        <div className="cookie-consent-options">
          <CookieConsentCategory
            category="necessary"
            checked
            disabled
            integrations={integrations}
          />
          {(['functional', 'analytics', 'marketing'] as const).map(category => (
            <CookieConsentCategory
              key={category}
              category={category}
              checked={draft[category]}
              integrations={integrations}
              onChange={checked => setDraft(current => ({ ...current, [category]: checked }))}
            />
          ))}
        </div>

        <div className="cookie-consent-dialog-actions">
          <button type="button" className="cookie-consent-button cookie-consent-button-secondary" onClick={closeSettings}>
            Anuluj
          </button>
          <button type="button" className="cookie-consent-button cookie-consent-button-primary" onClick={() => savePreferences(draft)}>
            Zapisz ustawienia
          </button>
          <button type="button" className="cookie-consent-button cookie-consent-button-plain" onClick={acceptAll}>
            Akceptuj wszystko
          </button>
        </div>
      </section>
    </div>
  );
}

function CookieBanner() {
  const { acceptAll, openSettings } = useConsent();

  return (
    <aside className="cookie-consent-banner" aria-label="Informacja o cookies">
      <div className="cookie-consent-banner-copy">
        <span className="cookie-consent-cookie-icon"><Cookie size={20} /></span>
        <div>
          <p>Dbamy o Twoją prywatność</p>
          <span>
            Używamy niezbędnych plików cookies do działania sklepu. Opcjonalne technologie włączamy tylko za Twoją zgodą - możesz je dostosować lub zaakceptować wszystkie.
          </span>
        </div>
      </div>
      <div className="cookie-consent-banner-actions">
        <button type="button" className="cookie-consent-button cookie-consent-button-secondary" onClick={openSettings}>
          <Settings2 size={15} /> Dostosuj
        </button>
        <button type="button" className="cookie-consent-button cookie-consent-button-primary" onClick={acceptAll}>
          Akceptuj wszystko
        </button>
      </div>
    </aside>
  );
}

export function CookieConsentManager() {
  const { hasSelection, isReady, settingsOpen } = useConsent();

  if (!isReady) return null;
  return (
    <>
      {!hasSelection && !settingsOpen && <CookieBanner />}
      {settingsOpen && <CookieSettingsPanel />}
    </>
  );
}

export function CookieSettingsButton({ className = 'cookie-consent-footer-link' }: { className?: string }) {
  const { openSettings } = useConsent();
  return (
    <button type="button" className={className} onClick={openSettings}>
      <ShieldCheck size={14} />
      Ustawienia cookies
    </button>
  );
}
